// MessageBoardPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc, getDocs, updateDoc, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import { db } from './firebase.js';

function MessageBoardPage({ user, isAdmin, isMobile }) {
  const [posts, setPosts] = useState([]);
  const [newPostTitle, setNewPostTitle] = useState('');
  const [newPostContent, setNewPostContent] = useState('');
  const [newReply, setNewReply] = useState({});
  const [editingPost, setEditingPost] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editContent, setEditContent] = useState('');
  const [error, setError] = useState(null);
  const [collapsedPosts, setCollapsedPosts] = useState({}); // Tracks collapsed state
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }
    fetchPosts();
  }, [user, navigate]);

  const fetchPosts = async () => {
    try {
      const postsSnapshot = await getDocs(collection(db, 'message_board'));
      const postsData = await Promise.all(postsSnapshot.docs.map(async (postDoc) => {
        const postData = { id: postDoc.id, ...postDoc.data() };
        const repliesSnapshot = await getDocs(collection(db, `message_board/${postDoc.id}/replies`));
        const replies = repliesSnapshot.docs.map(replyDoc => ({
          id: replyDoc.id,
          ...replyDoc.data()
        }));
        return { ...postData, replies: replies || [] };
      }));
      setPosts(postsData.sort((a, b) => b.createdAt?.seconds - a.createdAt?.seconds));
      // Set all posts to collapsed by default
      const initialCollapsedState = postsData.reduce((acc, post) => ({
        ...acc,
        [post.id]: true
      }), {});
      setCollapsedPosts(initialCollapsedState);
      setError(null);
    } catch (err) {
      console.error('Error fetching posts:', err);
      setError('Failed to load posts');
    }
  };

  const handlePostSubmit = async (e) => {
    e.preventDefault();
    if (!newPostTitle.trim() || !newPostContent.trim()) {
      setError('Title and content are required');
      return;
    }
    try {
      const docRef = await addDoc(collection(db, 'message_board'), {
        title: newPostTitle,
        content: newPostContent,
        authorId: user.uid,
        authorEmail: user.email,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      setNewPostTitle('');
      setNewPostContent('');
      setCollapsedPosts(prev => ({ ...prev, [docRef.id]: true })); // New post collapsed by default
      fetchPosts();
      setError(null);
    } catch (err) {
      console.error('Error posting message:', err);
      setError('Failed to post message');
    }
  };

  const handleReplySubmit = async (postId, e) => {
    e.preventDefault();
    if (!newReply[postId]?.trim()) return;
    try {
      await addDoc(collection(db, `message_board/${postId}/replies`), {
        content: newReply[postId],
        authorId: user.uid,
        authorEmail: user.email,
        createdAt: serverTimestamp()
      });
      setNewReply(prev => ({ ...prev, [postId]: '' }));
      fetchPosts();
    } catch (err) {
      console.error('Error posting reply:', err);
      setError('Failed to post reply');
    }
  };

  const handleEditPost = (post) => {
    setEditingPost(post.id);
    setEditTitle(post.title);
    setEditContent(post.content);
  };

  const handleUpdatePost = async (postId) => {
    if (!editTitle.trim() || !editContent.trim()) {
      setError('Title and content are required');
      return;
    }
    try {
      await updateDoc(doc(db, 'message_board', postId), {
        title: editTitle,
        content: editContent,
        updatedAt: serverTimestamp()
      });
      setEditingPost(null);
      setEditTitle('');
      setEditContent('');
      fetchPosts();
      setError(null);
    } catch (err) {
      console.error('Error updating post:', err);
      setError('Failed to update post');
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    try {
      await deleteDoc(doc(db, 'message_board', postId));
      fetchPosts();
    } catch (err) {
      console.error('Error deleting post:', err);
      setError('Failed to delete post');
    }
  };

  const toggleCollapse = (postId) => {
    setCollapsedPosts(prev => ({
      ...prev,
      [postId]: !prev[postId]
    }));
  };

  return (
    <div className={`message-board-page ${isMobile ? 'message-board-page-mobile' : ''}`}>
      Community Message Board
      {error && <p className="text-red-500">{error}</p>}
      <form onSubmit={handlePostSubmit} className="mb-4">
        <input
          type="text"
          value={newPostTitle}
          onChange={(e) => setNewPostTitle(e.target.value)}
          placeholder="Post title"
          className="modal-input w-full mb-2"
          maxLength="100"
        />
        <textarea
          value={newPostContent}
          onChange={(e) => setNewPostContent(e.target.value)}
          placeholder="Write a new post..."
          className="modal-textarea w-full"
          rows="4"
        />
        <button type="submit" className="modal-button modal-button-primary">
          Post Message
        </button>
      </form>

      <div className="posts-container">
        {posts.map(post => (
          <div key={post.id} className="post-card mb-4 p-4 bg-white rounded shadow">
            <div className="post-header flex justify-between items-center mb-2">
              <div>
                <div className="flex items-center">
                  <p className="font-bold mr-2">{post.authorEmail}</p>
                  <button
                    onClick={() => toggleCollapse(post.id)}
                    className="collapse-button"
                  >
                    {collapsedPosts[post.id] ? 'Expand' : 'Collapse'}
                  </button>
                </div>
                <p className="text-sm text-gray-500">
                  Posted: {post.createdAt?.toDate().toLocaleString()}
                  {post.updatedAt && post.updatedAt !== post.createdAt && 
                    ` (Edited: ${post.updatedAt?.toDate().toLocaleString()})`}
                  {collapsedPosts[post.id] && ` | Replies: ${post.replies.length}`}
                </p>
                <p className="font-semibold mt-1">{post.title}</p>
              </div>
              {(post.authorId === user.uid || isAdmin) && !collapsedPosts[post.id] && (
                <div>
                  {post.authorId === user.uid && (
                    <button
                      onClick={() => handleEditPost(post)}
                      className="modal-button modal-button-primary mr-2"
                    >
                      Edit
                    </button>
                  )}
                  <button
                    onClick={() => handleDeletePost(post.id)}
                    className="modal-button modal-button-danger"
                  >
                    Delete
                  </button>
                </div>
              )}
            </div>

            {!collapsedPosts[post.id] && (
              <>
                {editingPost === post.id ? (
                  <div className="mb-4">
                    <input
                      type="text"
                      value={editTitle}
                      onChange={(e) => setEditTitle(e.target.value)}
                      placeholder="Post title"
                      className="modal-input w-full mb-2"
                      maxLength="100"
                    />
                    <textarea
                      value={editContent}
                      onChange={(e) => setEditContent(e.target.value)}
                      className="modal-textarea w-full"
                      rows="4"
                    />
                    <button
                      onClick={() => handleUpdatePost(post.id)}
                      className="modal-button modal-button-primary mr-2"
                    >
                      Save
                    </button>
                    <button
                      onClick={() => setEditingPost(null)}
                      className="modal-button modal-button-secondary"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <p className="mb-4">{post.content}</p>
                )}

                <div className="replies ml-4">
                  {post.replies?.map(reply => (
                    <div key={reply.id} className="reply-card mb-2 p-2 bg-gray-100 rounded">
                      <p className="text-sm font-bold">{reply.authorEmail}</p>
                      <p className="text-sm text-gray-500">
                        Posted: {reply.createdAt?.toDate().toLocaleString()}
                      </p>
                      <p>{reply.content}</p>
                    </div>
                  ))}
                </div>

                <form onSubmit={(e) => handleReplySubmit(post.id, e)} className="mt-2">
                  <textarea
                    value={newReply[post.id] || ''}
                    onChange={(e) => setNewReply(prev => ({ ...prev, [post.id]: e.target.value }))}
                    placeholder="Write a reply..."
                    className="modal-textarea w-full"
                    rows="2"
                  />
                  <button type="submit" className="modal-button modal-button-primary">
                    Post Reply
                  </button>
                </form>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default MessageBoardPage;